import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Heart, MessageCircle, BadgeCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("conversations"); // "matches" or "conversations"

  useEffect(() => {
    fetchMatches();
    fetchConversations();
  }, []);

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/matches`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/conversations`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error("Fetch conversations error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: localStorage.getItem("language") === "en" ? undefined : fr });
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="matches-page">
      
      <div className="pt-6 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab("conversations")}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                activeTab === "conversations"
                  ? "bg-primary text-white"
                  : "bg-card/50 text-muted-foreground hover:bg-card"
              }`}
              data-testid="tab-conversations"
            >
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Messages ({conversations.length})
            </button>
            <button
              onClick={() => setActiveTab("matches")}
              className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                activeTab === "matches"
                  ? "bg-primary text-white"
                  : "bg-card/50 text-muted-foreground hover:bg-card"
              }`}
              data-testid="tab-matches"
            >
              <Heart className="w-4 h-4 inline mr-2" />
              {tx("Matchs (")}{matches.length})
            </button>
          </div>

          {/* Conversations Tab */}
          {activeTab === "conversations" && (
            <>
              {conversations.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                    <MessageCircle className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{tx("Pas de conversations")}</h3>
                  <p className="text-muted-foreground">
                    {tx("Envoyez un message à quelqu'un pour démarrer une conversation !")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conversations.map((conv, idx) => (
                    <div
                      key={conv.user.user_id || idx}
                      onClick={() => navigate(`/chat/${conv.user.user_id}`)}
                      className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:border-primary/50 transition-all"
                      data-testid={`conversation-${conv.user.user_id}`}
                    >
                      <div className="relative">
                        <Avatar className={`w-14 h-14 border-2 ${conv.is_match ? 'border-primary' : 'border-secondary'}`}>
                          <AvatarImage src={conv.user.picture || conv.user.photos?.[0]} />
                          <AvatarFallback className="bg-primary text-white">
                            {conv.user.name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        {conv.is_match && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full border-2 border-card flex items-center justify-center">
                            <Heart className="w-3 h-3 text-white fill-white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1">
                            <h3 className="font-semibold text-white truncate">
                              {conv.user.name}
                            </h3>
                            {conv.user.is_verified && (
                              <BadgeCheck className="w-4 h-4 text-green-500" />
                            )}
                            {conv.is_match && (
                              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                Match
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conv.last_message.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground truncate">
                          {conv.last_message.content_type === "image" ? "📷 Photo" : conv.last_message.content_type === "voice" ? tx("🎤 Message vocal") : conv.last_message.content}
                        </p>
                      </div>
                      
                      {conv.unread_count > 0 && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">{conv.unread_count}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Matches Tab */}
          {activeTab === "matches" && (
            <>
              {matches.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-12 h-12 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-2">{tx("Pas encore de matchs")}</h3>
                  <p className="text-muted-foreground">
                    {tx("Continuez à liker des profils pour avoir des matchs !")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {matches.map((match) => (
                    <div
                      key={match.match_id}
                      onClick={() => navigate(`/chat/${match.user.user_id}`)}
                      className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4 flex items-center gap-4 cursor-pointer hover:border-primary/50 transition-all"
                      data-testid={`match-${match.match_id}`}
                    >
                      <div className="relative">
                        <Avatar className="w-14 h-14 border-2 border-primary">
                          <AvatarImage src={match.user.picture || match.user.photos?.[0]} />
                          <AvatarFallback className="bg-primary text-white">
                            {match.user.name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-card" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1">
                            <h3 className="font-semibold text-white truncate">
                              {match.user.name}, {match.user.age || 25}
                            </h3>
                            {match.user.is_verified && (
                              <BadgeCheck className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          {match.last_message && (
                            <span className="text-xs text-muted-foreground">
                              {formatTime(match.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        
                        {match.last_message ? (
                          <p className="text-sm text-muted-foreground truncate">
                            {match.last_message.content_type === "image" ? "📷 Photo" : match.last_message.content_type === "voice" ? tx("🎤 Message vocal") : match.last_message.content}
                          </p>
                        ) : (
                          <p className="text-sm text-primary">
                            {tx("Nouveau match ! Dites bonjour 👋")}
                          </p>
                        )}
                      </div>
                      
                      <MessageCircle className="w-6 h-6 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
